import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatTooltip } from '@angular/material/tooltip';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { Utils } from 'src/app/core/resources/utils';
import { TypeMerchandiseService } from '../manual-creation-cargo/components/type-merchandise/type-merchandise.service';
import { ManualCreationCargoService } from '../manual-creation-cargo/manual-creation-cargo.service';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';
import { MerchandiseType } from 'src/app/core/interfaces/merchandiseType';
import { MerchandiseCodes } from 'src/app/core/enums/merchandiseCodes.enum';

@Component({
    selector: 'app-cargo-type-dangerous-merchandise',
    templateUrl: './cargo-type-dangerous-merchandise.component.html',
    styleUrls: ['./cargo-type-dangerous-merchandise.component.scss']
})
export class CargoTypeDangerousMerchandiseComponent implements OnInit {
    @Input() inputFormControl: FormControl;
    @Input() options: OptionsAutocomplete;
    @Input() validate: string = '';
    formControltypeMerchandise: FormControl = new FormControl();
    typeMerchandiseOptions: MerchandiseType[] = [];
    filteredTypesMerchandiseOptions: MerchandiseType[] = [];

    constructor(
        public utils: Utils,
        private spinner: NgxSpinnerService,
        private manualCreationCargoService: ManualCreationCargoService,
        private typeMerchandiseService: TypeMerchandiseService,
    ) { }

    get selectionType(): 'autocomplete' | 'select' {
        return this.options && this.options.selectionType ? this.options.selectionType : 'autocomplete';
    }
    get typeMerchandise(): MerchandiseCodes {
        return this.options && this.options.typeMerchandise ? this.options.typeMerchandise : MerchandiseCodes.BASIC_MERCHANDISE;
    }

    ngOnInit() {
        const validator = this.inputFormControl && this.inputFormControl.validator ? this.inputFormControl.validator({} as AbstractControl) : '';
        if (validator && validator.required) this.formControltypeMerchandise.setValidators(Validators.required);
        if (this.selectionType === 'select') this.getInitialTypesMerchandise();
        this.formControltypeMerchandise.valueChanges.subscribe(data => {
            if (data && typeof data === 'string' && data.trim().length) this.getTypesMerchandise(data.trim());
        });
    }

    private async getInitialTypesMerchandise() {
        const code = this.utils.getNestedValue(this.options, 'code');
        const hazardousType = this.utils.getNestedValue(this.options, 'hazardousType');
        let response = [];
        try {
            response = await this.typeMerchandiseService.searchMerchandise(null, this.typeMerchandise, hazardousType, code).toPromise();
        } catch (error) { }
        this.typeMerchandiseOptions = response && response.length ? response : [];
        this.filteredTypesMerchandiseOptions = this.typeMerchandiseOptions;
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes) {
            if (changes.options && changes.options.currentValue) {
                const initialTypeMerchandise = changes.options.currentValue.initialTypeMerchandise;
                if (initialTypeMerchandise)
                    this.setInitialTypeMerchandise(initialTypeMerchandise, false);
                const initialTypeMerchandiseToSearch = changes.options.currentValue.initialTypeMerchandiseToSearch;
                if (initialTypeMerchandiseToSearch)
                    this.setInitialTypeMerchandise(initialTypeMerchandiseToSearch, true);
                if (changes.options.currentValue.code && this.selectionType === 'select') {
                    await this.getInitialTypesMerchandise();
                    this.getTypesMerchandise('');
                }
                if (changes.options.currentValue.hazardousType) this.getTypesMerchandise('');

            }

            if (changes.validate) {
                switch (changes.validate.currentValue) {
                    case 'touched':
                        this.formControltypeMerchandise.markAsTouched();
                        break;
                    case 'untouched':
                        this.formControltypeMerchandise.markAsUntouched();
                        break;
                    case 'enable':
                        this.formControltypeMerchandise.enable();
                        break;
                    case 'disable':
                        this.formControltypeMerchandise.disable();
                        break;
                    case 'disable&untouched':
                        this.formControltypeMerchandise.markAsUntouched();
                        this.formControltypeMerchandise.disable();
                        break;
                    case 'setValidators':
                        this.formControltypeMerchandise.setValidators(Validators.required);
                        this.formControltypeMerchandise.updateValueAndValidity();
                        break;
                    case 'clearValidators':
                        this.formControltypeMerchandise.clearValidators();
                        this.formControltypeMerchandise.updateValueAndValidity();
                        break;
                    default:
                        break;
                }
            }
        }
    }

    private async setInitialTypeMerchandise(initialTypeMerchandise: { code: string, description: string }, search: boolean = false) {
        if (!initialTypeMerchandise) return;
        if (!initialTypeMerchandise.code || !initialTypeMerchandise.description) return this.formControltypeMerchandise.setValue('');

        if (!search && this.selectionType === 'autocomplete') return this.formControltypeMerchandise.setValue(initialTypeMerchandise);
        const valueToSearch = this.selectionType === 'select' ? null : this.getOptionVisible(initialTypeMerchandise as MerchandiseType);
        if (this.selectionType === 'select' && !this.typeMerchandiseOptions.length) await this.getInitialTypesMerchandise();
        await this.getTypesMerchandise(valueToSearch);
        if (this.filteredTypesMerchandiseOptions.length) {
            const initialTypeFound = this.filteredTypesMerchandiseOptions.find(option => option.description === initialTypeMerchandise.description);
            if (initialTypeFound) {
                this.formControltypeMerchandise.setValue(initialTypeFound);
                this.inputFormControl.setValue(initialTypeFound);
            }
        }

    }

    private async getTypesMerchandise(value: string) {
        if (this.selectionType === 'select') {
            this.filteredTypesMerchandiseOptions = this.typeMerchandiseOptions.filter(option => {
                if (!value) return true;
                return option && option.description && option.description.toLowerCase().includes(value.toLowerCase());
            });
            return;
        }
        const valueToSearch = value.toLowerCase();
        if (!valueToSearch) return this.filteredTypesMerchandiseOptions = [];
        const hasLocalFilter = [MerchandiseCodes.TARIFF].includes(this.typeMerchandise);
        const isUNCode = [MerchandiseCodes.UN_CODE].includes(this.typeMerchandise);
        const code = this.options && this.options.code ? this.options.code : isUNCode ? valueToSearch : null;
        const hazardousType = this.options && this.options.hazardousType ? this.options.hazardousType : null;
        try {
            const response = await this.typeMerchandiseService.searchMerchandise(hasLocalFilter || isUNCode ? null : valueToSearch, this.typeMerchandise, hazardousType, code).toPromise();
            this.filteredTypesMerchandiseOptions = response && response.length ? response : [];
        } catch (error) {
            this.filteredTypesMerchandiseOptions = [];
        }
    }

    changeValue() {
        if (this.inputFormControl) this.inputFormControl.setValue('');
    }

    public onSelectValue(value: MerchandiseType) {
        this.inputFormControl.setValue({ ...value });
    }

    public displayFn(data: string | MerchandiseType): string {
        if (!data) return '';
        if (typeof data === 'string') return data;
        if (data.type === MerchandiseCodes.UN_CODE) return data.code;
        return data.description;
    }

    public getOptionVisible(data: MerchandiseType): string {
        if (this.typeMerchandise === MerchandiseCodes.UN_CODE) return data.code;
        return data.description;
    }

    public returnFn(data: string | MerchandiseType): string | undefined {
        return data ? typeof data === 'string' ? data : data.code : undefined;
    }

    compareByDescription(a: { description: string }, b: { description: string }) {
        return a && a.description && b && b.description && a.description === b.description;
    }
} 